import { useState, useEffect, useCallback, useRef } from 'react';
import { apiEvents } from './useApiEvents';

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const normalizeResult = (result, defaultData) => {
  if (defaultData === undefined || defaultData === null) {
    return result ?? [];
  }
  if (Array.isArray(defaultData)) {
    return Array.isArray(result) ? result : [];
  }
  if (isPlainObject(defaultData)) {
    return isPlainObject(result) ? result : { ...defaultData };
  }
  return result ?? defaultData;
};

/**
 * Custom hook equivalent to a lightweight React Query useQuery.
 * Automatically refetches when dependencies change or when apiEvents.emit(key) is called.
 *
 * options:
 *  - enabled (bool)
 *  - pollingInterval (number)
 *  - defaultData (any)  -> returned while loading and on errors/normalization
 */
export const useApi = (key, fetcher, deps = [], options = {}) => {
  const { enabled = true, pollingInterval = null, defaultData } = options;
  const [data, setData] = useState(defaultData ?? []);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const fetcherRef = useRef(fetcher);
  const defaultDataRef = useRef(defaultData);

  // Callers pass an inline arrow function, so `fetcher` has a new identity on
  // every render. Keeping it in a ref stops it from invalidating `fetchData`,
  // which would otherwise retrigger the effect below on every render and loop
  // forever. Same for defaultData, which is often passed as a fresh [] literal.
  useEffect(() => {
    fetcherRef.current = fetcher;
    defaultDataRef.current = defaultData;
  });

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const result = await fetcherRef.current();
      setData(normalizeResult(result, defaultDataRef.current));
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error(`[useApi:${key || 'anonymous'}]`, err);
        setError(err);
        setData(defaultDataRef.current ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, key]);

  useEffect(() => {
    fetchData();

    if (key) {
      const unsubscribe = apiEvents.subscribe(key, fetchData);
      return () => {
        unsubscribe();
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, fetchData, ...deps]);

  useEffect(() => {
    if (!pollingInterval) return;
    const intervalId = setInterval(fetchData, pollingInterval);
    return () => clearInterval(intervalId);
  }, [fetchData, pollingInterval]);

  const refetch = useCallback(() => {
    if (key) {
      apiEvents.emit(key);
    } else {
      fetchData();
    }
  }, [key, fetchData]);

  const refresh = refetch;

  return { data, loading, error, refetch, refresh };
};

/**
 * Invalidate one or more resource keys to trigger refetch everywhere they are used.
 */
export const invalidateApiCache = (keyOrKeys) => {
  const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];
  (Array.isArray(keys) ? keys : []).forEach((key) => apiEvents.emit(key));
};
